<template>
  <ModuleContainer>
    <slot />
  </ModuleContainer>
</template>

<script>
/* eslint-disable no-unused-vars */
import Feature from "@mapbox/mapbox-gl-draw/src/feature_types/feature";
import { DrawingType } from "./DrawingType";

import MapboxDraw from "@mapbox/mapbox-gl-draw";

import { rectangleMode } from "./modes/rectangle/RectangleMode";
import { circleMode } from "./modes/circle/CircleMode";

import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import { styles } from "./style";
import ModuleMixin from "@components/Map/mixins/ModuleMixin";
//import MapControlGroupButton from "@components/Map/control/MapControlGroupButton.vue";
//import MapControlButton from "@components/Map/control/MapControlButton.vue";
/**
 * Mapbox Draw API: https://github.com/mapbox/mapbox-gl-draw/blob/master/docs/API.md
 */
import drawControl, {
  activeDraw,
  clearDraw,
  onDrawCreated,
  onDrawUpdated,
  onDrawDeleted,
  draw,
  saveDraw
} from "@components/Map/store/store-draw";

export default {
  mixins: [ModuleMixin],

  props: {
    additionModes: {
      type: Object,
      default: undefined
    }
  },

  provide() {
    return {
      getDrawControl: this.getDrawControl,
      activeDraw: this.activeDraw,
      draw: this.draw,
      saveDraw: this.saveDraw,
      cancelDraw: this.cancelDraw,
      deleteSelectedDraw: this.deleteSelectedDraw,
      clearDraw: this.clearDraw,
      activeEditDraw: this.activeEditDraw
    };
  },

  data() {
    return {
      bindedOnDrawCreated: null,
      bindedOnDrawDeleted: null,
      bindedOnDrawUpdated: null,
      bindedOnDrawCombined: null,
      bindedOnDrawUnCombined: null,
      bindedOnDrawSelectionChanged: null,
      bindedOnDrawModeChanged: null,
      bindedOnDrawRender: null,
      bindedOnDrawActionable: null,
      bindedOnKeyDown: null
    };
  },
  computed: { drawControl },
  methods: {
    onInit() {
      this.addControl(
        new MapboxDraw({
          displayControlsDefault: false,
          modes: Object.assign(
            {},
            MapboxDraw.modes,
            rectangleMode,
            circleMode,
            this.additionModes
          ),
          styles
        })
      );

      this.initDrawControl();
    },

    onDestroy() {
      this.destroyDrawControl();
    },

    getDrawControl() {
      return this.getControl();
    },

    initDrawControl() {
      this.bindedOnDrawCreated = onDrawCreated.bind(this);
      this.bindedOnDrawDeleted = onDrawDeleted.bind(this);
      this.bindedOnDrawUpdated = onDrawUpdated.bind(this);

      this.bindedOnDrawCombined = this.onDrawCombined.bind(this);
      this.bindedOnDrawUnCombined = this.onDrawUnCombined.bind(this);

      this.bindedOnDrawSelectionChanged =
        this.onDrawSelectionChanged.bind(this);

      this.bindedOnDrawModeChanged = this.onDrawModeChanged.bind(this);
      this.bindedOnDrawRender = this.onDrawRender.bind(this);
      this.bindedOnDrawActionable = this.onDrawActionable.bind(this);
      this.bindedOnKeyDown = this.onKeyDown.bind(this);

      this.map.on("draw.create", this.bindedOnDrawCreated);
      this.map.on("draw.delete", this.bindedOnDrawDeleted);
      this.map.on("draw.update", this.bindedOnDrawUpdated);

      this.map.on("draw.combine", this.bindedOnDrawCombined);
      this.map.on("draw.uncombine", this.bindedOnDrawUnCombined);

      this.map.on("draw.selectionchange", this.bindedOnDrawSelectionChanged);

      this.map.on("draw.modechange", this.bindedOnDrawModeChanged);
      this.map.on("draw.render", this.bindedOnDrawRender);
      this.map.on("draw.actionable", this.bindedOnDrawActionable);

      this.map.getContainer().addEventListener("keydown", this.bindedOnKeyDown);
    },

    destroyDrawControl() {
      this.map.off("draw.create", this.bindedOnDrawCreated);
      this.map.off("draw.delete", this.bindedOnDrawDeleted);
      this.map.off("draw.update", this.bindedOnDrawUpdated);

      this.map.off("draw.combine", this.bindedOnDrawCombined);
      this.map.off("draw.uncombine", this.bindedOnDrawUnCombined);

      this.map.off("draw.selectionchange", this.bindedOnDrawSelectionChanged);

      this.map.off("draw.modechange", this.bindedOnDrawModeChanged);
      this.map.off("draw.render", this.bindedOnDrawRender);
      this.map.off("draw.actionable", this.bindedOnDrawActionable);

      this.map
        .getContainer()
        .removeEventListener("keydown", this.bindedOnKeyDown);
    },

    /**
     *
     * @param {object} geojson
     */
    activeDraw(geojson) {
      this.bringToFront_();
      activeDraw(geojson);
    },
    /**
     *
     * @param {object} geojson
     */
    activeEditDraw(geojson) {
      this.drawControl.activated = true;
      let featureIds = this.control.add(geojson);
      this.control.changeMode("direct_select", { featureId: featureIds[0] });

      this.bringToFront_();
    },

    /**
     *
     * @param {DrawingType} type
     * @param {({ added: { [string]: object }, updated: { [string]: object }, deleted: { [string]: object } }) => void} [callback]
     * @param {object} [options]
     */
    draw(type, callback, options) {
      draw(type, callback, options);
    },

    /**
     *
     * @param {({ added: { [string]: object }, updated: { [string]: object }, deleted: { [string]: object }, geojson: object }) => void} callback
     */
    saveDraw(callback) {
      saveDraw(callback);
    },

    /**
     *
     */
    cancelDraw() {
      this.clearDraw();
    },

    /**
     * Invokes the current mode's trash action. Returns the draw instance for chaining.
     *
     * In simple_select mode, this deletes all selected features.
     *
     * In direct_select mode, this deletes the selected vertices.
     *
     * In drawing modes, this cancels drawing and reverts Draw to simple_select mode.
     *
     * If you want to delete features regardless of the current mode, use the delete or deleteAll function.
     *
     * More: https://github.com/mapbox/mapbox-gl-draw/blob/main/docs/API.md#trash-draw
     */
    deleteSelectedDraw() {
      this.control.trash();
    },

    /**
     *
     */
    clearDraw() {
      clearDraw();
    },

    /**
     *
     * @param {object} event
     * @param {Array<Feature>} event.deletedFeatures Array of deleted features (those incorporated into new multifeatures)
     * @param {Array<Feature>} event.createdFeatures Array of created multifeatures
     */
    onDrawCombined(event) {},

    /**
     *
     * @param {object} event
     * @param {Array<Feature>} event.deletedFeatures Array of deleted multifeatures (split into features)
     * @param {Array<Feature>} event.createdFeatures Array of created features
     */
    onDrawUnCombined(event) {},

    /**
     *
     * @param {object} event
     * @param {Array<Feature>} event.features Array of features that are selected after the change
     */
    onDrawSelectionChanged(event) {},

    /**
     *
     * @param {object} event
     * @param {string} event.mode The next mode, i.e. the mode that Draw is changing to
     */
    onDrawModeChanged(event) {},

    /**
     *
     */
    onDrawRender() {},

    /**
     *
     * @param {object} event
     * @param {object} event.actions
     * @param {boolean} event.actions.trash
     * @param {boolean} event.actions.combineFeatures
     * @param {boolean} event.actions.uncombineFeatures
     */
    onDrawActionable(event) {},

    /**
     *
     * @param {object} event
     */
    onKeyDown(event) {
      const KEYS = {
        BACKSPACE: 8,
        DELETE: 46,
        ESCAPE: 27,
        ENTER: 13
      };

      if (event.keyCode === KEYS.BACKSPACE || event.keyCode === KEYS.DELETE) {
        this.deleteSelectedDraw();
      } else if (event.keyCode === KEYS.ESCAPE) {
        this.cancelDraw();
      }
    },

    /**
     * @private
     */
    bringToFront_() {
      const glDrawLayerIds = Object.values(
        this.map.style._serializedLayers
      ).reduce((layers, l) => {
        if (
          l.source === "mapbox-gl-draw-cold" ||
          l.source === "mapbox-gl-draw-hot"
        ) {
          layers.push(l.id);
        }

        return layers;
      }, []);

      for (const id of glDrawLayerIds) {
        this.map.moveLayer(id);
      }
    },
    addControl(controlInstance) {
      this.drawControl.control = controlInstance;
      this.control = controlInstance;
      this.map.addControl(this.control);
    },

    removeControl() {
      if (!this.control) return;
      this.drawControl.control = null;

      this.map.removeControl(this.control);
      this.control = null;
    },
    getControl() {
      if (this.loaded) {
        return Promise.resolve(this.control);
      } else {
        return new Promise((resolve, reject) => {
          this.$once("module-loaded", () => {
            resolve(this.control);
          });

          this.$once("module-failed", reject);
        });
      }
    }
  }
};
</script>
